/* <rr-variant-picker> — resuelve la variante elegida, la deja en la URL y
   emite rr:variant para rr-atc, la barra fija y la galería.
   Solo variantes reales: las NUMBERED también desde el 27 sep 2026 (del 26 al
   27 llevaron talla y piedra como propiedades de línea). */
(() => {
  if (customElements.get('rr-variant-picker')) return;

  class RRVariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-rr-variants]');
      if (!data) return;
      try {
        this.variants = JSON.parse(data.textContent);
      } catch {
        return;
      }
      this.addEventListener('change', (e) => {
        if (!e.target.matches('[data-rr-option]')) return;
        e.target.closest('fieldset')?.classList.remove('is-invalid');
        this.update();
        this.measure(e.target);
      });
      // el botón de compra (o la barra fija) sin talla elegida: se lleva al cliente a ella
      document.addEventListener('rr:need-size', () => this.askSize());
      this.remembered();
    }

    // Talla sin elegir (snippet size-pending, 18 oct 2026): la marca, la pone en
    // pantalla y abre el desplegable donde se puede (Chrome y Android; en iOS
    // se queda en foco). Va en el mismo clic que el botón: sin él no se abre.
    askSize() {
      const group = [...this.querySelectorAll('fieldset')].find((g, i) => this.selectedOptions()[i] == null);
      if (!group) return;
      group.classList.add('is-invalid');
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      group.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' });
      const control = group.querySelector('select[data-rr-option], input[data-rr-option]:not([data-soldout])');
      if (!control) return;
      control.focus({ preventScroll: true });
      if (control.tagName === 'SELECT' && control.showPicker) {
        try { control.showPicker(); } catch {}
      }
    }

    // Talla medida en otra ficha (rr-ring-sizer, localStorage rr-ring-mm): si la
    // URL no trae variante, preselecciona la talla de anillo más cercana,
    // sumando el ajuste de las piezas anchas (data-fit-mm).
    remembered() {
      if (new URL(location.href).searchParams.has('variant')) return;
      let mm = 0;
      try { mm = parseFloat(localStorage.getItem('rr-ring-mm')); } catch {}
      if (!(mm > 10)) return;
      const target = mm + (parseFloat(this.dataset.fitMm) || 0);
      for (const select of this.querySelectorAll('select[data-rr-option]')) {
        let best = null;
        let bestDiff = 0.6;
        for (const o of select.options) {
          const d = parseFloat((o.value.split(' · ')[2] || '').replace(' MM', ''));
          if (!o.disabled && d > 0 && Math.abs(d - target) <= bestDiff) { best = o; bestDiff = Math.abs(d - target); }
        }
        if (!best) continue;
        select.value = best.value;
        const note = document.createElement('p');
        note.className = 'rr-vpicker__remembered';
        note.textContent = `${this.dataset.rememberedLabel || ''} ${best.value.split(' · ').slice(0, 2).join(' · ')}`.trim();
        select.after(note);
        this.update();
      }
    }

    // Medición (docs/plan-de-medicion.md §4.5): qué opción cambia y, si es la talla, cómo se
    // eligió (a mano o con el medidor). El valor es el código fijo de snippets/analytics-option
    // (us9, spn…), igual en todos los idiomas. Una talla puesta al cargar no cuenta como elegida.
    measure(control) {
      const kind = control.closest('fieldset')?.dataset.rrKind;
      const opt = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      const value = opt && opt.dataset.rrCode;
      const method = control.dataset.rrMethod || 'manual';
      delete control.dataset.rrMethod;
      if (!kind || !value || !window.rrTrack) return;
      const size = kind === 'ring' || kind === 'bracelet';
      // option_value y no value: en GA4 value es el valor económico del evento
      window.rrTrack('rr_variant_change', { option_kind: size ? 'size' : kind, size_kind: size ? kind : undefined, option_value: value });
      if (size) window.rrTrack('rr_size_selected', { size_kind: kind, size_value: value, size_method: method });
    }

    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((g) => {
        const list = g.querySelector('select[data-rr-option]');
        if (list) return list.value || null;
        const checked = g.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    update() {
      const opts = this.selectedOptions();
      // Con la talla aún sin elegir, la piedra sigue mandando en las fotos: se
      // avisa con una variante de esa piedra (la primera que se vende) y
      // pending, que el botón de compra lee como "falta la talla". La URL no
      // cambia: al recargar, la talla tiene que seguir sin elegir.
      if (opts.includes(null)) {
        const fits = (v) => v.options.every((o, i) => opts[i] == null || o === opts[i]);
        const variant = this.variants.find((v) => fits(v) && v.available) || this.variants.find(fits);
        if (variant) document.dispatchEvent(new CustomEvent('rr:variant', { detail: { variant, pending: true } }));
        return;
      }
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      if (!variant) return;

      if (this.dataset.url) {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }

      document.dispatchEvent(new CustomEvent('rr:variant', { detail: { variant } }));
    }
  }

  customElements.define('rr-variant-picker', RRVariantPicker);
})();
